import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Header } from "@/components/Header";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { SettingsEditor } from "@/components/admin/SettingsEditor";
import { BlogManager } from "@/components/admin/BlogManager";
import { FacilityCreator } from "@/components/admin/FacilityCreator";
import { OrdersManager } from "@/components/admin/OrdersManager";
import { ClaimsManager } from "@/components/admin/ClaimsManager";
import { FacilitiesManager } from "@/components/admin/FacilitiesManager";
import { IntegrationsManager } from "@/components/admin/IntegrationsManager";
import { AnalyticsDashboard } from "@/components/admin/AnalyticsDashboard";
import { AuditLog } from "@/components/admin/AuditLog";
import { UsersManager } from "@/components/admin/UsersManager";
import { DangerZone } from "@/components/admin/DangerZone";
import { Button } from "@/components/ui/button";
import { Palette, FileText, Building2, ShieldCheck, MessageSquare, ArrowRight, ClipboardList, Plus, Pencil, BarChart3, Plug, ScrollText, Users } from "lucide-react";

export default function AdminConsole() {
  const { user, loading } = useAuth();
  const [tab, setTab] = useState("analytics");
  const [prefill, setPrefill] = useState(null);
  const [pendingOrderId, setPendingOrderId] = useState(null);
  const [ordersRefresh, setOrdersRefresh] = useState(0);
  const [facMode, setFacMode] = useState("manage");

  if (!loading && (!user || user.role !== "admin")) {
    return <div className="min-h-screen bg-[#F7F6F2]"><Header /><div className="text-center py-24 text-[#5C6660]">This area is for administrators only.</div></div>;
  }

  const buildFromOrder = (order) => {
    setPrefill({
      name: order.facility_name || "",
      contact_email: order.email || "",
      owner_email: "",
      tier: "premium",
      description: order.notes || "",
      telephone: order.phone || "",
    });
    setPendingOrderId(order.request_id);
    setTab("facilities");
    setFacMode("add");
    toast.info("Order loaded — add the address and details, then publish. Assign the director's account email if they have one.");
  };

  const onFacilityCreated = async () => {
    if (pendingOrderId) {
      try { await api.post(`/service-requests/${pendingOrderId}/fulfill`, {}); }
      catch (e) { console.error("Failed to mark concierge order fulfilled", e); }
      setPendingOrderId(null);
      setPrefill(null);
      setOrdersRefresh((n) => n + 1);
      toast.success("Concierge order marked fulfilled.");
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F6F2]">
      <Header />
      <div className="max-w-[1200px] mx-auto px-4 md:px-8 py-10">
        <h1 className="font-heading text-3xl font-medium text-[#1C211F]">Admin Dashboard</h1>
        <p className="text-[#4A534E] mt-1 mb-8">Manage site content & design, publish blog posts, fulfill concierge orders, and add facilities.</p>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-[#EFEEE8] mb-8 flex-wrap h-auto">
            <TabsTrigger value="analytics" data-testid="tab-analytics"><BarChart3 className="h-4 w-4 mr-1.5" /> Analytics</TabsTrigger>
            <TabsTrigger value="content" data-testid="tab-content"><Palette className="h-4 w-4 mr-1.5" /> Content & Design</TabsTrigger>
            <TabsTrigger value="integrations" data-testid="tab-integrations"><Plug className="h-4 w-4 mr-1.5" /> Integrations & Pricing</TabsTrigger>
            <TabsTrigger value="blog" data-testid="tab-blog"><FileText className="h-4 w-4 mr-1.5" /> Blog</TabsTrigger>
            <TabsTrigger value="claims" data-testid="tab-claims"><ShieldCheck className="h-4 w-4 mr-1.5" /> Claims</TabsTrigger>
            <TabsTrigger value="orders" data-testid="tab-orders"><ClipboardList className="h-4 w-4 mr-1.5" /> Concierge Orders</TabsTrigger>
            <TabsTrigger value="facilities" data-testid="tab-facilities"><Building2 className="h-4 w-4 mr-1.5" /> Facilities</TabsTrigger>
            <TabsTrigger value="users" data-testid="tab-users"><Users className="h-4 w-4 mr-1.5" /> Users</TabsTrigger>
            <TabsTrigger value="audit" data-testid="tab-audit"><ScrollText className="h-4 w-4 mr-1.5" /> Audit Log</TabsTrigger>
          </TabsList>

          <TabsContent value="analytics"><AnalyticsDashboard /></TabsContent>
          <TabsContent value="content"><SettingsEditor /></TabsContent>
          <TabsContent value="integrations"><IntegrationsManager /></TabsContent>
          <TabsContent value="blog"><BlogManager /></TabsContent>
          <TabsContent value="claims"><ClaimsManager /></TabsContent>
          <TabsContent value="orders"><OrdersManager onBuild={buildFromOrder} refreshKey={ordersRefresh} /></TabsContent>
          <TabsContent value="facilities">
            <div className="flex gap-2 mb-6">
              <Button onClick={() => setFacMode("manage")} variant={facMode === "manage" ? "default" : "outline"} className={`rounded-xl ${facMode === "manage" ? "bg-[#1A365D] hover:bg-[#12283F] text-white" : "border-[#E8E6DF]"}`} data-testid="fac-mode-manage"><Pencil className="h-4 w-4 mr-1.5" /> Manage all facilities</Button>
              <Button onClick={() => setFacMode("add")} variant={facMode === "add" ? "default" : "outline"} className={`rounded-xl ${facMode === "add" ? "bg-[#1A365D] hover:bg-[#12283F] text-white" : "border-[#E8E6DF]"}`} data-testid="fac-mode-add"><Plus className="h-4 w-4 mr-1.5" /> Add new facility</Button>
            </div>
            {facMode === "manage" ? <FacilitiesManager /> : <FacilityCreator prefill={prefill} onCreated={onFacilityCreated} />}
          </TabsContent>
          <TabsContent value="audit"><AuditLog /></TabsContent>
          <TabsContent value="users"><UsersManager /></TabsContent>
        </Tabs>

        <div className="mt-12 border-t border-[#E8E6DF] pt-8">
          <h2 className="font-heading text-lg font-medium text-[#1C211F] mb-4">Operations</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <button onClick={() => setTab("claims")} className="text-left bg-white border border-[#E8E6DF] rounded-2xl p-5 hover:shadow-lg hover:-translate-y-0.5 transition-all" data-testid="link-claims">
              <ShieldCheck className="h-6 w-6 text-[#0077B6] mb-2" />
              <div className="font-medium text-[#1C211F] flex items-center gap-1">Verify claims <ArrowRight className="h-3.5 w-3.5" /></div>
              <p className="text-sm text-[#5C6660]">Approve directors who claim listings.</p>
            </button>
          </div>
          <DangerZone />
        </div>
      </div>
    </div>
  );
}
